import assetLocationsService from "../../../../services/assetService/assetLocationsService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CACHE_KEY_ASSETLOCATIONS } from "../../../../utils/constants";
import { AssetLocationInterface } from "../../../../models/AssetModel/AssetLocation";

interface UpdateAssLocationsContext {
  previousAssLocations: AssetLocationInterface[];
}

const useUpdateAssetLocation = (page: number, pageSize: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    AssetLocationInterface,
    Error,
    AssetLocationInterface,
    UpdateAssLocationsContext
  >({
    mutationFn: async (assLocation: AssetLocationInterface) => {
      // Assuming the ID is stored in the ticket object or can be derived
      const { id, ...assLocationData } = assLocation;
      return assetLocationsService.Update(id || "", assLocationData);
    },

    onMutate: async (updatedAssLocation: AssetLocationInterface) => {
      await queryClient.cancelQueries({
        queryKey: [CACHE_KEY_ASSETLOCATIONS, page, pageSize],
      });

      const previousAssLocations =
        queryClient.getQueryData<AssetLocationInterface[]>([
          CACHE_KEY_ASSETLOCATIONS,
          page,
          pageSize,
        ]) || [];

      queryClient.setQueryData<AssetLocationInterface[]>(
        [CACHE_KEY_ASSETLOCATIONS, page, pageSize],
        (assLocations = []) =>
          assLocations.map((assLocation) =>
            assLocation.id === updatedAssLocation.id
              ? updatedAssLocation
              : assLocation
          )
      );

      return { previousAssLocations };
    },

    onSuccess: (savedAssLocation) => {
      queryClient.setQueryData<AssetLocationInterface[]>(
        [CACHE_KEY_ASSETLOCATIONS, page, pageSize],
        (assLocations) =>
          assLocations?.map((assLocation) =>
            assLocation.id === savedAssLocation.id
              ? savedAssLocation
              : assLocation
          )
      );
    },

    onError: (error, updatedAssLocation, context) => {
      if (!context) return;

      queryClient.setQueryData<AssetLocationInterface[]>(
        [CACHE_KEY_ASSETLOCATIONS, page, pageSize],
        context.previousAssLocations
      );
    },
  });
};

export default useUpdateAssetLocation;
